import { forwardRef, useContext, useEffect, useState } from 'react';
import type { PropsWithoutRef, Ref } from 'react';

import Text from '../Text';
import Link from '../Link';
import type { BaseProps, ForwardRefForwardPropsComponent } from '../../types';
import NoValue from '../NoValue/NoValue';

import LocationView from './LocationView';
import type { LocationViewProps } from './LocationView';
import MapsContext from './MapsContext';
import type { LatLng } from './Location.types';
import { loadMapsAPI } from './utils';

export interface LocationDisplayProps extends BaseProps {
  /** Location address or coordinates */
  value?: string | 'current' | LatLng;
  /** The text to be displayed. */
  displayText?: string;
  /**
   * Controls styling of the field.
   * @default 'text-map'
   */
  variant?: 'text-map' | 'text' | 'map';
  /**
   * Height of the map container.
   * @default '25rem'
   */
  height?: LocationViewProps['height'];
  /**
   * How much the map should zoom.
   * @default 13
   */
  zoomLevel?: LocationViewProps['zoomLevel'];
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLAnchorElement | HTMLSpanElement>;
}

const LocationDisplay: ForwardRefForwardPropsComponent<LocationDisplayProps> = forwardRef(
  function LocationDisplay(
    {
      value,
      displayText,
      variant = 'text-map',
      height = '25rem',
      zoomLevel = 13,
      ...restProps
    }: PropsWithoutRef<LocationDisplayProps>,
    ref: LocationDisplayProps['ref']
  ) {
    const { name, ...providerOpts } = useContext(MapsContext);
    const [location, setLocation] = useState<LatLng>();
    const [loading, setLoading] = useState(false);

    const valueString =
      typeof value === 'string' ? value : `${value?.latitude}, ${value?.longitude}`;

    useEffect(() => {
      if (['text-map', 'map'].includes(variant) && value) {
        (async () => {
          setLoading(true);
          const mapAPIObj = await loadMapsAPI(name, providerOpts);
          if (mapAPIObj) {
            setLocation(await mapAPIObj.getCoords(value));
          }
          setLoading(false);
        })();
      }
    }, [variant, value]);

    return !value ? (
      <Text ref={ref as Ref<HTMLSpanElement>} {...restProps} aria-hidden>
        <NoValue />
      </Text>
    ) : (
      <>
        {(variant === 'text' || variant === 'text-map') &&
          (name === 'google' ? (
            <Link
              ref={ref as Ref<HTMLAnchorElement>}
              {...restProps}
              target='_blank'
              rel='noopener noreferrer'
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                typeof value === 'string' ? value : `${value?.latitude}, ${value?.longitude}`
              )}`}
            >
              {displayText || valueString}
            </Link>
          ) : (
            <Text ref={ref as Ref<HTMLSpanElement>} {...restProps}>
              {displayText || valueString}
            </Text>
          ))}
        {(variant === 'text-map' || variant === 'map') && (
          <LocationView
            pins={location ? [location] : []}
            centerMapOnChange
            height={height}
            zoomLevel={zoomLevel}
            loading={loading}
          />
        )}
      </>
    );
  }
);

export default LocationDisplay;
